import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import lockApi from '../services/lockApi';
import guestArrivalApi from '../services/guestArrivalApi';

// Stable empty report so consumers never crash before the first fetch / on error
// (mirrors EMPTY_REPORT in useCheckinMonitor.js and EMPTY_ALERTS in useAlerts.js).
//
// ⚠️ headline defaults to 'unverified', NOT 'ready'. The whole point of this
// surface is that it never shows a green it hasn't earned — a pre-fetch or
// errored state must not read as "today is fine".
const EMPTY_REPORT = {
  headline: 'unverified',
  lockStateAsOf: null,
  count: 0,
  summary: {
    total: 0, ready: 0, action: 0, unverified: 0, observations: 0, noLock: 0,
    arrivals: 0, overallAction: 0, overallUnverified: 0, overallReady: 0,
    messageAction: 0, messagePending: 0, messageNa: 0,
    cleaningAction: 0, cleaningUnverified: 0, cleaningReady: 0,
    lockOverallAction: 0, lockOverallUnverified: 0, lockOverallReady: 0
  },
  cleaningDegraded: false,
  arrivals: []
};

export const ARRIVAL_READINESS_QUERY_KEY = ['arrival-readiness-today'];

// Today's MERGED arrival readiness for the admin dashboard card (Phase 4,
// tasks/lock-health-assertion-phase4.md): lock-code column + check-in-message
// column + one overall verdict per guest. Server recomputes on each fetch from
// the same engines the morning email uses. Refreshes on the dashboard's 5-min
// cadence so a code that lands mid-morning clears the card without a reload.
export const useArrivalReadiness = () => {
  const result = useQuery({
    queryKey: ARRIVAL_READINESS_QUERY_KEY,
    queryFn: () => lockApi.getTodayArrivalReadiness(),
    refetchInterval: 5 * 60 * 1000,
    staleTime: 60 * 1000
  });

  return {
    ...result,
    report: result.data ?? EMPTY_REPORT
  };
};

/**
 * Acknowledge that a Gathern / manual / direct booking's check-in message was sent
 * by hand (tasks/arrival-readiness-action-side.md §7.7).
 *
 * Invalidates the readiness query rather than patching the row locally: the
 * message column is a SERVER verdict composed from two engines, and a client-side
 * guess at the new state is exactly how a surface starts disagreeing with the
 * report it is supposed to mirror.
 */
export const useUndoCheckinMessageSentManually = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: lockApi.undoCheckinMessageSentManually,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ARRIVAL_READINESS_QUERY_KEY });
    },
    onError: (error) => {
      console.error('❌ Failed to undo the manual-send acknowledgement:', error);
    }
  });
};

export const useMarkCheckinMessageSentManually = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: lockApi.markCheckinMessageSentManually,
    // onSettled, not onSuccess (matching useClaimAlert): a refusal here means the
    // client's view was STALE — the row offered a tick for a booking the server
    // says is automated — so the failure case is precisely when a refetch is most
    // needed. Invalidating only on success would leave the bad row on screen.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ARRIVAL_READINESS_QUERY_KEY });
    },
    onError: (error) => {
      console.error('❌ Failed to mark check-in message as sent manually:', error);
    }
  });
};

/**
 * Ops check-in confirmation (tasks/manual-guest-arrival-confirm.md) — record / withdraw.
 * Invalidate, never patch: whether the row still offers the link is a server verdict.
 * onSettled, not onSuccess: a refusal means the row was stale, which is when a refetch matters most.
 */
export const useRecordGuestArrival = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bookingId, observedAt }) => guestArrivalApi.recordGuestArrival(bookingId, observedAt),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ARRIVAL_READINESS_QUERY_KEY });
    },
    onError: (error) => {
      console.error('❌ Failed to record the check-in confirmation:', error);
    }
  });
};

export const useUndoGuestArrival = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bookingId) => guestArrivalApi.undoGuestArrival(bookingId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ARRIVAL_READINESS_QUERY_KEY });
    },
    onError: (error) => {
      console.error('❌ Failed to undo the check-in confirmation:', error);
    }
  });
};
